/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Link, useHistory } from "react-router-dom";
import React, { MouseEventHandler, useCallback } from "react";
import { AppPath } from "components/AppPath";
import { useCurrentUser } from "actions/CurrentUserActions";

const useAuthenticatedNavigation = (to: string): VoidFunction => {
  const { currentUser } = useCurrentUser();
  const history = useHistory();
  return useCallback(() => {
    if (!currentUser) {
      window.adobeIMS?.signIn({
        redirect_uri: `${window.location.origin}${to}`,
      });
    } else {
      history.push(to);
    }
  }, [currentUser, history, to]);
};

export const AuthenticatedLink: React.FC<{ to: string }> = ({
  to,
  children,
}) => {
  const navigate = useAuthenticatedNavigation(to);

  const onClick: MouseEventHandler = useCallback(
    (event) => {
      event.preventDefault();
      navigate();
    },
    [navigate]
  );

  return (
    <a href={to} onClick={onClick}>
      {children}
    </a>
  );
};

export const useNavigateToStudio = (): { navigateToStudio: VoidFunction } => {
  const navigate = useAuthenticatedNavigation(AppPath.STUDIO);
  return { navigateToStudio: navigate };
};
